import type { EditorJsBlock, EditorJsData } from '../../editorjs-types';

import { documentViewHref } from '#utils/document-app-path';
import { isValidDocumentPath } from '#utils/document-path';

/**
 * App routes that are not document paths. Root-relative links under these are
 * left unchanged (e.g. `/settings`, `/d/...`, legacy `/documents/...`).
 *
 * New document routes use `/d/view/{path}` (see `App.tsx`); older saved links
 * may still be `/documents/...` and are left as-is.
 */
const RESERVED_PATH_PREFIXES = [
  '/d',
  '/documents',
  '/processors',
  '/instance',
  '/prompts',
  '/ai-audit',
  '/uploads',
  '/settings',
  /** Uploads API, GraphQL, OAuth callbacks — not document paths. */
  '/api',
] as const;

function pathnameIsReserved(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return true;

  for (const prefix of RESERVED_PATH_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return true;
    }
  }
  return false;
}

/**
 * If `href` is a same-origin path that matches {@link isValidDocumentPath} but
 * is not already under a reserved app prefix, returns `/d/view/{path}` plus
 * any query/hash. Otherwise returns `null` (leave the link as-is).
 */
export function rewriteEditorAnchorHrefIfDocumentPath(
  href: string,
  documentPathRegex: string,
  baseOrigin: string,
): string | null {
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith('#')) return null;

  if (
    /^mailto:/i.test(trimmed) ||
    /^tel:/i.test(trimmed) ||
    /^javascript:/i.test(trimmed)
  ) {
    return null;
  }

  let pathname: string;
  let suffix: string;

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const u = new URL(trimmed);
      if (u.origin !== baseOrigin) return null;

      pathname = u.pathname;
      suffix = `${u.search}${u.hash}`;
    } else if (trimmed.startsWith('//')) {
      return null;
    } else if (trimmed.startsWith('/')) {
      const u = new URL(trimmed, `${baseOrigin}/`);
      pathname = u.pathname;
      suffix = `${u.search}${u.hash}`;
    } else {
      return null;
    }
  } catch {
    return null;
  }

  if (pathnameIsReserved(pathname)) return null;

  const docPath = pathname.replace(/^\//, '');
  if (!isValidDocumentPath(docPath, documentPathRegex)) return null;

  return `${documentViewHref(docPath)}${suffix}`;
}

/**
 * Rewrites `href="..."` in an HTML fragment produced by Editor.js (inline
 * links in headers, paragraphs, list items, etc.).
 */
export function normalizeDocumentLinksInHtml(
  html: string,
  documentPathRegex: string,
  baseOrigin: string,
): string {
  if (!html.includes('href')) return html;

  return html.replace(
    /\bhref\s*=\s*(["'])([^"']*)\1/gi,
    (full, quote: string, url: string) => {
      const next = rewriteEditorAnchorHrefIfDocumentPath(
        url,
        documentPathRegex,
        baseOrigin,
      );
      if (!next || next === url) return full;

      return `href=${quote}${next}${quote}`;
    },
  );
}

function normalizeListBlockData(
  data: Record<string, unknown>,
  documentPathRegex: string,
  baseOrigin: string,
): void {
  if (typeof data.content === 'string') {
    data.content = normalizeDocumentLinksInHtml(
      data.content,
      documentPathRegex,
      baseOrigin,
    );
  }

  const items = data.items;
  if (!Array.isArray(items) || items.length === 0) return;

  const first = items[0];
  if (typeof first === 'string') {
    data.items = items.map((entry) =>
      typeof entry === 'string'
        ? normalizeDocumentLinksInHtml(entry, documentPathRegex, baseOrigin)
        : entry,
    );
    return;
  }

  if (
    first &&
    typeof first === 'object' &&
    'checked' in first &&
    'text' in (first as object)
  ) {
    data.items = (items as Array<{ text?: unknown; checked?: unknown }>).map(
      (item) => {
        if (typeof item.text === 'string') {
          return {
            ...item,
            text: normalizeDocumentLinksInHtml(
              item.text,
              documentPathRegex,
              baseOrigin,
            ),
          };
        }

        return item;
      },
    );
    return;
  }

  for (const entry of items) {
    if (entry && typeof entry === 'object') {
      normalizeListBlockData(
        entry as Record<string, unknown>,
        documentPathRegex,
        baseOrigin,
      );
    }
  }
}

function normalizeDocumentLinksInBlock(
  block: EditorJsBlock,
  documentPathRegex: string,
  baseOrigin: string,
): EditorJsBlock {
  if (block.type === 'code') {
    return block;
  }

  const data = block.data;
  if (!data || typeof data !== 'object') {
    return block;
  }

  const d = data as Record<string, unknown>;

  if (block.type === 'list') {
    const cloned = { ...d };
    normalizeListBlockData(cloned, documentPathRegex, baseOrigin);
    return { ...block, data: cloned };
  }

  if (block.type === 'table') {
    const content = d.content;
    if (!Array.isArray(content) || content.length === 0) {
      return block;
    }

    const cloned = { ...d } as Record<string, unknown>;
    cloned.content = content.map((row) => {
      if (!Array.isArray(row)) return row;

      return row.map((cell) => {
        if (typeof cell !== 'string' || !cell.includes('<a ')) return cell;

        return normalizeDocumentLinksInHtml(
          cell,
          documentPathRegex,
          baseOrigin,
        );
      });
    });
    return { ...block, data: cloned };
  }

  if (typeof d.text === 'string' && d.text.includes('<a ')) {
    return {
      ...block,
      data: {
        ...d,
        text: normalizeDocumentLinksInHtml(
          d.text,
          documentPathRegex,
          baseOrigin,
        ),
      },
    };
  }

  return block;
}

/** Returns a shallow copy of `data` with blocks normalized (safe for React state). */
export function normalizeDocumentLinksInEditorJsData(
  data: EditorJsData,
  documentPathRegex: string,
  baseOrigin: string,
): EditorJsData {
  return {
    ...data,
    blocks: data.blocks.map((block) =>
      normalizeDocumentLinksInBlock(block, documentPathRegex, baseOrigin),
    ),
  };
}
